/**
 * The Wren Valley grid: the simulator behind the power course at `/grid`
 * (docs/grid.md).
 *
 * A small town's distribution network — a 33/11 kV substation, two 11 kV
 * feeders, and an 11/0.4 kV transformer for each street — solved the way
 * distribution engineers solve radial networks: a backward/forward sweep
 * with complex currents and voltages, per unit on a 1 MVA base. A day is 96
 * quarter-hour solves against each load's daily profile.
 *
 * It is a real power flow, not a slider formula: change a tap at the
 * substation and every street's voltage moves; move a normally-open point
 * and the load, the currents and the voltage drop all go somewhere else.
 * Everything is deterministic, so a level's verdict is a pure function of
 * the settings the learner leaves, here, in the tests, and on a server.
 *
 * Simplified on purpose, and the levels say so: balanced three-phase only,
 * constant-power loads, taps set by hand (a real on-load tap changer moves
 * by itself), and no protection or frequency yet.
 */

/* ── Complex numbers ───────────────────────────────────────────────────── */

export interface C {
  re: number;
  im: number;
}

const c = (re: number, im = 0): C => ({ re, im });
const add = (a: C, b: C): C => c(a.re + b.re, a.im + b.im);
const sub = (a: C, b: C): C => c(a.re - b.re, a.im - b.im);
const mul = (a: C, b: C): C => c(a.re * b.re - a.im * b.im, a.re * b.im + a.im * b.re);
const scale = (a: C, k: number): C => c(a.re * k, a.im * k);
const conj = (a: C): C => c(a.re, -a.im);
const abs = (a: C): number => Math.hypot(a.re, a.im);
const div = (a: C, b: C): C => {
  const d = b.re * b.re + b.im * b.im;
  return c((a.re * b.re + a.im * b.im) / d, (a.im * b.re - a.re * b.im) / d);
};

/* ── The network ───────────────────────────────────────────────────────── */

/** Per-unit base. */
const S_BASE_MVA = 1;
const zBase = (kv: number) => (kv * kv) / S_BASE_MVA;
/** Amps that one per-unit of current is, at a voltage level. */
export const iBase = (kv: number) => (S_BASE_MVA * 1e6) / (Math.sqrt(3) * kv * 1e3);

export type BusId = 'supply' | 'sub' | 'a1' | 'a2' | 'a3' | 'b1' | 'b2' | 'mill' | 'oak' | 'elm' | 'harbour' | 'station';

export interface Bus {
  id: BusId;
  kv: number;
  name: string;
}

export const BUSES: Bus[] = [
  { id: 'supply', kv: 33, name: 'Grid supply' },
  { id: 'sub', kv: 11, name: 'Wren Valley substation' },
  { id: 'a1', kv: 11, name: 'Feeder A, pole 1' },
  { id: 'a2', kv: 11, name: 'Feeder A, pole 2' },
  { id: 'a3', kv: 11, name: 'Feeder A, pole 3' },
  { id: 'b1', kv: 11, name: 'Feeder B, pole 1' },
  { id: 'b2', kv: 11, name: 'Feeder B, pole 2' },
  { id: 'mill', kv: 0.4, name: 'Mill Lane' },
  { id: 'oak', kv: 0.4, name: 'Oak Road' },
  { id: 'elm', kv: 0.4, name: 'Elm Street' },
  { id: 'harbour', kv: 0.4, name: 'Harbour Row' },
  { id: 'station', kv: 0.4, name: 'Station Square' },
];

export const STREETS = ['mill', 'oak', 'elm', 'harbour', 'station'] as const;
export type StreetId = (typeof STREETS)[number];

/** Overhead line and cable types an 11 kV section can be built with. */
export const CONDUCTORS = {
  light: { name: 'Light overhead (95 mm²)', r: 0.32, x: 0.35, amps: 250, costPerKm: 0 },
  heavy: { name: 'Heavy overhead (185 mm²)', r: 0.16, x: 0.33, amps: 400, costPerKm: 60_000 },
  cable: { name: 'Underground cable (300 mm²)', r: 0.1, x: 0.12, amps: 450, costPerKm: 150_000 },
} as const;
export type ConductorId = keyof typeof CONDUCTORS;
export const CONDUCTOR_IDS = Object.keys(CONDUCTORS) as ConductorId[];

export type LineId = 'sub-a1' | 'a1-a2' | 'a2-a3' | 'sub-b1' | 'b1-b2' | 'tie';
export type TxId = 'T1' | 'mill' | 'oak' | 'elm' | 'harbour' | 'station';

interface LineDef {
  id: LineId;
  from: BusId;
  to: BusId;
  km: number;
  name: string;
}

export const LINES: LineDef[] = [
  { id: 'sub-a1', from: 'sub', to: 'a1', km: 2, name: 'Feeder A, section 1' },
  { id: 'a1-a2', from: 'a1', to: 'a2', km: 3, name: 'Feeder A, section 2' },
  { id: 'a2-a3', from: 'a2', to: 'a3', km: 3.5, name: 'Feeder A, section 3' },
  { id: 'sub-b1', from: 'sub', to: 'b1', km: 2, name: 'Feeder B, section 1' },
  { id: 'b1-b2', from: 'b1', to: 'b2', km: 1.5, name: 'Feeder B, section 2' },
  // The normally-open tie between the ends of the two feeders.
  { id: 'tie', from: 'a3', to: 'b2', km: 1.5, name: 'Tie, A3 to B2' },
];

interface TxDef {
  id: TxId;
  from: BusId;
  to: BusId;
  mva: number;
  /** Impedance in percent on its own rating. */
  zPct: number;
  name: string;
}

export const TRANSFORMERS: TxDef[] = [
  { id: 'T1', from: 'supply', to: 'sub', mva: 12, zPct: 8, name: 'Substation transformer, 33/11 kV' },
  { id: 'mill', from: 'a1', to: 'mill', mva: 1.6, zPct: 5, name: 'Mill Lane transformer' },
  { id: 'oak', from: 'a2', to: 'oak', mva: 1.6, zPct: 5, name: 'Oak Road transformer' },
  { id: 'elm', from: 'a3', to: 'elm', mva: 1.6, zPct: 5, name: 'Elm Street transformer' },
  { id: 'harbour', from: 'b1', to: 'harbour', mva: 1.6, zPct: 5, name: 'Harbour Row transformer' },
  { id: 'station', from: 'b2', to: 'station', mva: 1.6, zPct: 5, name: 'Station Square transformer' },
];

/** The substation's on-load tap changer: 1.25% a step. */
export const OLTC_STEP = 0.0125;
export const OLTC_MIN = -8;
export const OLTC_MAX = 8;
/** A street transformer's off-load taps, as a boost to the street's voltage. */
export const STREET_TAPS = [-0.05, -0.025, 0, 0.025, 0.05] as const;

/* ── Loads ─────────────────────────────────────────────────────────────── */

/** Share of peak, hour by hour from midnight. */
const HOMES = [0.32, 0.28, 0.26, 0.25, 0.26, 0.3, 0.42, 0.55, 0.52, 0.45, 0.42, 0.42, 0.44, 0.42, 0.4, 0.42, 0.5, 0.68, 0.88, 1.0, 0.95, 0.8, 0.6, 0.42];
const WORK = [0.2, 0.2, 0.2, 0.2, 0.2, 0.25, 0.45, 0.75, 0.95, 1.0, 1.0, 0.98, 0.9, 0.98, 1.0, 0.95, 0.85, 0.6, 0.4, 0.3, 0.25, 0.22, 0.2, 0.2];

export const PROFILES = { homes: HOMES, work: WORK } as const;

interface StreetLoad {
  /** Peak demand, MW. */
  mw: number;
  pf: number;
  profile: keyof typeof PROFILES;
}

export const STREET_LOADS: Record<StreetId, StreetLoad> = {
  mill: { mw: 1.0, pf: 0.92, profile: 'work' },
  oak: { mw: 0.85, pf: 0.95, profile: 'homes' },
  elm: { mw: 1.3, pf: 0.95, profile: 'homes' },
  harbour: { mw: 1.0, pf: 0.95, profile: 'homes' },
  station: { mw: 0.8, pf: 0.95, profile: 'homes' },
};

/** The quarter-hour steps of a day. */
export const STEPS = 96;
export const stepHour = (k: number) => k / 4;

function profileAt(profile: readonly number[], hour: number): number {
  const h = Math.floor(hour) % 24;
  const f = hour - Math.floor(hour);
  return (profile[h] ?? 0) * (1 - f) + (profile[(h + 1) % 24] ?? 0) * f;
}

/* ── The EV charging hub (level 2) ─────────────────────────────────────── */

/** Energy the hub must deliver each night, MWh, starting at 17:00. */
export const HUB_MWH = 12;
export const HUB_START = 17;
/** The cars leave at 07:00. */
export const HUB_DEADLINE = 7;
export const HUB_POWER_STOPS = [1, 1.5, 2, 2.5, 3] as const;

/** Hours after 17:00 the hub charges at full power; may run past midnight. */
export function hubHours(mw: number): number {
  return HUB_MWH / mw;
}

/** The hub's demand at an hour of the day, MW. */
export function hubAt(mw: number, hour: number): number {
  const since = (hour - HUB_START + 24) % 24;
  return since < hubHours(mw) ? mw : 0;
}

/** Does the hub finish before the cars leave? */
export function hubOnTime(mw: number): boolean {
  return hubHours(mw) <= ((HUB_DEADLINE - HUB_START + 24) % 24) + 1e-9;
}

/* ── Settings: what a learner can change ───────────────────────────────── */

export interface GridSettings {
  /** Substation tap position, OLTC_MIN..OLTC_MAX. */
  oltc: number;
  /** Each street transformer's tap, as an index into STREET_TAPS. */
  taps: Record<StreetId, number>;
  conductors: Record<Exclude<LineId, 'tie'>, ConductorId>;
  /** Which switches are closed. The tie is normally open. */
  closed: Record<LineId, boolean>;
  /** The EV hub: null when it is not built, else its charging power, MW. */
  hubMw: number | null;
}

export const DEFAULT_SETTINGS: GridSettings = {
  // Substations run their 11 kV bus a few percent high, to leave room for the drop.
  oltc: 3,
  taps: { mill: 2, oak: 2, elm: 2, harbour: 2, station: 2 },
  conductors: { 'sub-a1': 'light', 'a1-a2': 'light', 'a2-a3': 'light', 'sub-b1': 'light', 'b1-b2': 'light' },
  closed: { 'sub-a1': true, 'a1-a2': true, 'a2-a3': true, 'sub-b1': true, 'b1-b2': true, tie: false },
  hubMw: null,
};

/** What it costs to build these settings, against the defaults: conductor upgrades only. */
export function cost(s: GridSettings): number {
  let total = 0;
  for (const line of LINES) {
    if (line.id === 'tie') continue;
    const k = s.conductors[line.id];
    total += CONDUCTORS[k].costPerKm * line.km;
  }
  return total;
}

/* ── The solve ─────────────────────────────────────────────────────────── */

interface Branch {
  id: LineId | TxId;
  kind: 'line' | 'tx';
  parent: BusId;
  child: BusId;
  /** Series impedance, per unit, on the parent side. */
  z: C;
  /** Voltage ratio, child over parent (1 for a line). */
  t: number;
  /** Rating, per unit current on the parent side. */
  ratingPu: number;
  kv: number;
}

export type Topology = { ok: true; order: BusId[]; branches: Branch[] } | { ok: false; reason: 'loop' | 'island'; buses?: BusId[] };

/** The tree the closed switches make, from the supply outward, or why there is none. */
export function topology(s: GridSettings): Topology {
  const edges: { id: LineId | TxId; a: BusId; b: BusId; kind: 'line' | 'tx' }[] = [];
  for (const t of TRANSFORMERS) edges.push({ id: t.id, a: t.from, b: t.to, kind: 'tx' });
  for (const l of LINES) if (s.closed[l.id]) edges.push({ id: l.id, a: l.from, b: l.to, kind: 'line' });

  const seen = new Set<BusId>(['supply']);
  const order: BusId[] = ['supply'];
  const branches: Branch[] = [];
  const usedEdges = new Set<string>();
  for (let i = 0; i < order.length; i++) {
    const at = order[i] as BusId;
    for (const e of edges) {
      if (usedEdges.has(e.id)) continue;
      const other = e.a === at ? e.b : e.b === at ? e.a : null;
      if (!other) continue;
      usedEdges.add(e.id);
      if (seen.has(other)) return { ok: false, reason: 'loop' };
      seen.add(other);
      order.push(other);
      branches.push(makeBranch(e.id, e.kind, at, other, s));
    }
  }
  const island = BUSES.map((b) => b.id).filter((id) => !seen.has(id));
  if (island.length) return { ok: false, reason: 'island', buses: island };
  return { ok: true, order, branches };
}

function makeBranch(id: LineId | TxId, kind: 'line' | 'tx', parent: BusId, child: BusId, s: GridSettings): Branch {
  if (kind === 'tx') {
    const t = TRANSFORMERS.find((x) => x.id === id)!;
    const kv = BUSES.find((b) => b.id === t.from)!.kv;
    const zpu = (t.zPct / 100) * (S_BASE_MVA / t.mva);
    // Mostly reactive, as transformers are: X/R about 8.
    const z = c(zpu / Math.hypot(1, 8), (zpu * 8) / Math.hypot(1, 8));
    const ratio = id === 'T1' ? 1 + s.oltc * OLTC_STEP : 1 + (STREET_TAPS[s.taps[id as StreetId]] ?? 0);
    return { id, kind, parent, child, z, t: ratio, ratingPu: t.mva / S_BASE_MVA, kv };
  }
  const l = LINES.find((x) => x.id === id)!;
  const cond = id === 'tie' ? CONDUCTORS.light : CONDUCTORS[s.conductors[id as Exclude<LineId, 'tie'>]];
  const zb = zBase(11);
  const z = c((cond.r * l.km) / zb, (cond.x * l.km) / zb);
  return { id, kind, parent, child, z, t: 1, ratingPu: cond.amps / iBase(11), kv: 11 };
}

export interface Snapshot {
  converged: boolean;
  /** Voltage magnitude per bus, per unit. */
  v: Record<BusId, number>;
  /** Branch loading, a share of its rating. */
  loading: Record<string, number>;
  /** Branch current, amps on the parent side. */
  amps: Record<string, number>;
  /** Total demand and losses, MW. */
  demandMw: number;
  lossMw: number;
}

/** Each street's load at an hour, MW and Mvar, plus the hub on Station Square's 11 kV pole. */
export function loadsAt(s: GridSettings, hour: number): Partial<Record<BusId, C>> {
  const out: Partial<Record<BusId, C>> = {};
  for (const id of STREETS) {
    const l = STREET_LOADS[id];
    const p = l.mw * profileAt(PROFILES[l.profile], hour);
    const q = p * Math.tan(Math.acos(l.pf));
    out[id] = c(p / S_BASE_MVA, q / S_BASE_MVA);
  }
  if (s.hubMw !== null) {
    const p = hubAt(s.hubMw, hour);
    // Chargers run near unity power factor.
    out.b2 = add(out.b2 ?? c(0), c(p / S_BASE_MVA, (p * 0.1) / S_BASE_MVA));
  }
  return out;
}

/** One power flow: the backward/forward sweep. */
export function solve(s: GridSettings, hour: number, topo?: Topology): Snapshot {
  const tree = topo ?? topology(s);
  const v = {} as Record<BusId, number>;
  const loading: Record<string, number> = {};
  const amps: Record<string, number> = {};
  if (!tree.ok) {
    for (const b of BUSES) v[b.id] = 0;
    return { converged: false, v, loading, amps, demandMw: 0, lossMw: 0 };
  }
  const loads = loadsAt(s, hour);
  const V = new Map<BusId, C>(tree.order.map((b) => [b, c(1)]));
  const childrenOf = new Map<BusId, Branch[]>();
  for (const br of tree.branches) childrenOf.set(br.parent, [...(childrenOf.get(br.parent) ?? []), br]);
  const I = new Map<string, C>();

  let converged = false;
  for (let iter = 0; iter < 60; iter++) {
    // Backward: currents from the leaves toward the supply.
    const into = new Map<BusId, C>();
    for (let k = tree.order.length - 1; k >= 0; k--) {
      const bus = tree.order[k] as BusId;
      const S = loads[bus] ?? c(0);
      let current = conj(div(S, V.get(bus)!));
      for (const br of childrenOf.get(bus) ?? []) current = add(current, I.get(br.id)!);
      into.set(bus, current);
      const parentBranch = tree.branches.find((b) => b.child === bus);
      // A tap changes the current by its ratio: primary = t × secondary.
      if (parentBranch) I.set(parentBranch.id, scale(current, parentBranch.t));
    }
    // Forward: voltages from the supply outward.
    let worst = 0;
    for (const br of tree.branches) {
      const up = V.get(br.parent)!;
      const next = scale(sub(up, mul(I.get(br.id)!, br.z)), br.t);
      worst = Math.max(worst, abs(sub(next, V.get(br.child)!)));
      V.set(br.child, next);
    }
    if (worst < 1e-9) {
      converged = true;
      break;
    }
    if (!Number.isFinite(worst) || worst > 10) break;
  }

  let demand = 0;
  for (const S of Object.values(loads)) demand += S?.re ?? 0;
  let supplied = 0;
  for (const br of tree.branches.filter((b) => b.parent === 'supply')) supplied += mul(V.get('supply')!, conj(I.get(br.id)!)).re;
  for (const b of BUSES) v[b.id] = abs(V.get(b.id) ?? c(0));
  for (const br of tree.branches) {
    const i = abs(I.get(br.id)!);
    loading[br.id] = i / br.ratingPu;
    amps[br.id] = i * iBase(br.kv);
  }
  const ok = converged && BUSES.every((b) => v[b.id] > 0.5 && v[b.id] < 1.5);
  return { converged: ok, v, loading, amps, demandMw: demand * S_BASE_MVA, lossMw: (supplied - demand) * S_BASE_MVA };
}

export interface Day {
  ok: true;
  steps: Snapshot[];
}

export type DayResult = Day | { ok: false; reason: 'loop' | 'island' | 'collapse'; buses?: BusId[]; hour?: number };

/** The whole day, a quarter-hour at a time. */
export function runDay(s: GridSettings): DayResult {
  const topo = topology(s);
  if (!topo.ok) return { ok: false, reason: topo.reason, buses: topo.buses };
  const steps: Snapshot[] = [];
  for (let k = 0; k < STEPS; k++) {
    const snap = solve(s, stepHour(k), topo);
    if (!snap.converged) return { ok: false, reason: 'collapse', hour: stepHour(k) };
    steps.push(snap);
  }
  return { ok: true, steps };
}

/** A clock time for a quarter-hour step: `19:15`. */
export function clock(step: number): string {
  const h = Math.floor(stepHour(step)) % 24;
  const m = (step % 4) * 15;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
